import { AutocompleteWithVirtualList } from '@modules/common/components/AutocompleteWithVirtualList';
import { useFetchEqlFieldValues } from '@modules/common/hooks';
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  Box,
  FilterOptionsState,
  Stack,
  TextFieldProps,
  Typography,
  useControlled,
} from '@mui/material';
import { EqlFieldValue } from 'generated/graphql';
import { debounce } from 'lodash';
import { ReactNode, SyntheticEvent, useMemo, useRef, useState } from 'react';
import { useNotificationContext } from '../Notification/NotificationContext';
import { EqlFieldValuesAutoCompleteInput } from './EqlFieldValuesAutoCompleteInput';
import { EqlFieldValuesAutoCompletePopper } from './EqlFieldValuesAutoCompletePopper';
import { EqlFieldValuesVirtualListRow } from './EqlFieldValuesVirtualListRow';

export interface EqlFieldValuesAutoCompleteProps<Multiple extends boolean>
  extends Omit<
    AutocompleteProps<EqlFieldValue, Multiple, true, false>,
    'onChange' | 'options' | 'renderInput'
  > {
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  onChange?: (value: EqlFieldValue | EqlFieldValue[] | null) => void;
  filterField: string;
  fieldDisplayName: string;
  renderListHeader?: () => ReactNode;
  inputVariant?: TextFieldProps['variant'];
}

type toggleValues = 'all' | 'selected';

const RECOMMENDED_SEARCH_TERM_CHAR_LENGTH = 8;

export function EqlFieldValuesAutoComplete<Multiple extends boolean>({
  multiple,
  onChange,
  onClose,
  value: valueFromStore,
  renderInput,
  filterField,
  fieldDisplayName,
  size = 'small',
  inputValue: inputValueProp,
  onInputChange: onInputChangeProp,
  disableClearable = true,
  inputVariant = 'standard',
  ...autoCompleteProps
}: EqlFieldValuesAutoCompleteProps<Multiple>) {
  const [selectionMode, setSelectionMode] = useState<toggleValues>('all');
  const [inputValue, setInputValue] = useControlled({
    controlled: inputValueProp,
    default: '',
    name: 'EqlFieldValuesAutoComplete',
    state: 'inputValue',
  });
  const [searchTerm, setSearchTerm] = useState(inputValue);
  const [fetchEQLFieldValues, { cursor, values: options, isLoading: loading }] =
    useFetchEqlFieldValues({
      eqlField: filterField,
      searchTerm,
      fetchPolicy: 'cache-and-network',
    });
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  const { showNotification } = useNotificationContext();

  const onValueChange = (
    value: EqlFieldValue | EqlFieldValue[] | null,
    reason?: AutocompleteChangeReason,
    event?: SyntheticEvent<Element, Event>,
  ) => {
    if (reason === 'clear') {
      return;
    }

    if (value && 'length' in value && value.length > 100) {
      showNotification({
        contentComponent: 'Max 100 values can be selected',
        props: { severity: 'warning', zIndex: 10000 },
      });
      return;
    }

    // don't select/unselect an option when user presses ctrl + enter. Execute with existing values
    if (
      event &&
      'code' in event &&
      'ctrlKey' in event &&
      'metaKey' in event &&
      event.code === 'Enter' &&
      (event.ctrlKey || event.metaKey)
    ) {
      return onChange?.(valueFromStore ?? null);
    }

    onChange?.(value);
  };

  const areOptionsAndValueSame = (
    options: EqlFieldValue[],
    value: EqlFieldValue[],
  ) => {
    if (!options.length || options.length > value.length) {
      return false;
    }
    const optionsValues = options.map((option) => option.value);
    const valueValues = value.map((valueItem) => valueItem.value);

    return optionsValues.every((optionValue) =>
      valueValues.includes(optionValue),
    );
  };

  const allSelected =
    valueFromStore &&
    'length' in valueFromStore &&
    areOptionsAndValueSame(options, valueFromStore) &&
    !cursor;

  const someSelected =
    valueFromStore &&
    'length' in valueFromStore &&
    valueFromStore.length > 0 &&
    !allSelected;

  const toggleSelectAll = () => {
    if (allSelected || someSelected) {
      onValueChange([]);
    } else {
      if (options.length >= 100) {
        showNotification({
          contentComponent: 'First 100 values were selected',
          props: { severity: 'warning', zIndex: 10000 },
        });
      }
      onValueChange(options.slice(0, 100));
    }
  };

  const debounceSearchTerm = useMemo(
    () =>
      debounce((searchTerm: string) => {
        setSearchTerm(searchTerm);
      }, 250),
    [],
  );

  const onInputChange = (
    eve: SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === 'input') {
      setInputValue(value);
      debounceSearchTerm(value);
      onInputChangeProp?.(eve, value, reason);
    }
  };

  const filterOptions = (
    options: EqlFieldValue[],
    state: FilterOptionsState<EqlFieldValue>,
  ) => {
    if (inputValue && selectionMode === 'all') {
      return options;
    }

    if (selectionMode === 'all') {
      return options.filter((option) =>
        option.displayName
          ? option.displayName
              .toLowerCase()
              .includes(state.inputValue.toLowerCase())
          : option.value,
      );
    }
    if (Array.isArray(valueFromStore)) {
      return valueFromStore.filter((valueItem) =>
        valueItem.displayName
          ? valueItem.displayName
              .toLowerCase()
              .includes(state.inputValue.toLowerCase())
          : valueItem.value,
      );
    }
    return options;
  };

  const defaultRenderInput = (params: AutocompleteRenderInputParams) => (
    <Box ref={params.InputProps.ref}>
      <EqlFieldValuesAutoCompleteInput
        baseInputParams={params}
        shouldAllowMultipleValues={!!multiple}
        toggleSelectAll={toggleSelectAll}
        selectionMode={selectionMode}
        setSelectionMode={setSelectionMode}
        fieldDisplayName={fieldDisplayName}
        allSelected={allSelected}
        someSelected={someSelected}
        disableSelectAll={loading || !options.length}
        inputVariant={inputVariant}
      />
    </Box>
  );

  const getNoOptionsText = () => {
    const defaultNoOptionsTextNode = (
      <Stack direction="row" spacing={1.5}>
        <Box>🔎</Box>
        <Typography variant="inherit">
          {selectionMode === 'all' ? (
            `No results found for "${searchTerm}"`
          ) : (
            <Box>
              No <Box component="b">Selected</Box> value(s) found for "
              {searchTerm}"
            </Box>
          )}
        </Typography>
      </Stack>
    );

    if (searchTerm.length === 0) {
      const hasSelectedValues =
        Array.isArray(valueFromStore) && valueFromStore.length > 0;
      const isMetadataField = filterField.startsWith('metadata.');
      return hasSelectedValues && isMetadataField
        ? 'The selected metadata field has been marked as invisible, and cannot be modified.'
        : defaultNoOptionsTextNode;
    }

    if (searchTerm.length < RECOMMENDED_SEARCH_TERM_CHAR_LENGTH) {
      return 'Please enter a few more characters...';
    }

    return defaultNoOptionsTextNode;
  };

  return (
    <AutocompleteWithVirtualList
      {...autoCompleteProps}
      disableListWrap
      disableClearable={disableClearable}
      inputValue={inputValue}
      size={size}
      itemSize={size === 'small' ? 36 : 48}
      autoHighlight
      fullWidth
      multiple={multiple}
      value={valueFromStore}
      options={options}
      loading={loading}
      popupIcon={false}
      disableCloseOnSelect={multiple}
      getOptionLabel={(option) => option.displayName ?? option.value}
      renderInput={renderInput ?? defaultRenderInput}
      onInputChange={onInputChange}
      hasNextPage={
        Boolean(cursorRef.current) &&
        !loading &&
        selectionMode === 'all' &&
        !inputValue
      }
      loadMoreItems={async () => {
        await fetchEQLFieldValues({
          cursor: cursorRef.current,
          searchTerm,
        });
      }}
      renderOption={(props, option, state) =>
        [props, option, state] as React.ReactNode
      }
      componentsProps={{
        paper: {
          elevation: 8,
          sx: {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
        },
      }}
      isOptionEqualToValue={(option, value) => option.value === value?.value}
      onChange={(event, value, reason) => {
        onValueChange(value ?? null, reason, event);
      }}
      filterOptions={(options, state) => filterOptions(options, state)}
      onClose={(event, reason) => {
        if (reason !== 'toggleInput') {
          onClose?.(event, reason);
        }
      }}
      PopperComponent={EqlFieldValuesAutoCompletePopper}
      VirtualListRow={(props) => (
        <EqlFieldValuesVirtualListRow
          {...props}
          shouldShowCheckbox={!!multiple}
        />
      )}
      noOptionsText={getNoOptionsText()}
    />
  );
}
